import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { directorApi } from '@/services/api/director'
import type {
  DirectorConfig, DirectorHistoryItem, DirectorPendingItem,
} from '@/types/director'
import { extractErrorMessage } from '@/utils/errorHandler'
import { CaseListPane, type CaseListTab } from '@/components/director/casefile'
import { CaseDetailPane } from '@/components/director/casefile'
import { LoadingSpinner } from '@/components/common'
import { AlertCircle } from 'lucide-react'

/**
 * Fallakte v2 (P6.1/E1/E5): «Alle Fälle» als Master-Detail.
 *
 * - Links: gruppierte Fallliste (Offen/Verlauf, Suche, Frist-Gruppen)
 * - Rechts: Fallakte (Infoline, Fakten, Lektionen, Dokumente, Timeline, Aktionen)
 * - Route-Param :id = Auswahl (Deep-Links bleiben erhalten)
 * - Mobile (< lg): Detail als Overlay (E5)
 */
export function DirectorCasesPage() {
  const navigate = useNavigate()
  const { id: routeId } = useParams<{ id?: string }>()

  const [pending, setPending] = useState<DirectorPendingItem[]>([])
  const [history, setHistory] = useState<DirectorHistoryItem[]>([])
  const [config, setConfig] = useState<DirectorConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<CaseListTab>('pending')
  const [refreshKey, setRefreshKey] = useState(0)

  const loadPending = useCallback(async () => {
    try {
      const data = await directorApi.getPending()
      setPending(data)
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Ausstehende Anfragen konnten nicht geladen werden' }))
    }
  }, [])

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const data = await directorApi.getHistory()
      setHistory(data)
    } catch {
      // Verlauf ist sekundär — Still Failure ohne Banner
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const results = await Promise.allSettled([
          directorApi.getPending(),
          directorApi.getConfig(),
        ])
        if (cancelled) return
        if (results[0].status === 'fulfilled') setPending(results[0].value)
        else setError(extractErrorMessage(results[0].reason, { loadError: 'Fälle konnten nicht geladen werden' }))
        if (results[1].status === 'fulfilled') setConfig(results[1].value)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Verlauf lazy: genau einmal laden, auch wenn er leer bleibt (v3 B5)
  const historyLoadedRef = useRef(false)
  useEffect(() => {
    if (activeTab === 'history' && !historyLoadedRef.current && !historyLoading) {
      historyLoadedRef.current = true
      void loadHistory()
    }
  }, [activeTab, historyLoading, loadHistory])

  const casePending = useMemo(
    () => pending.filter(p => !p.kind || p.kind === 'request'),
    [pending],
  )

  // Auswahl: Deep-Link-Param steuert die Detail-Ansicht
  const selectedId = routeId ?? null
  const selectCase = useCallback((caseId: string) => {
    navigate(`/director/decisions/${caseId}`)
  }, [navigate])
  const deselect = useCallback(() => {
    navigate('/director/decisions')
  }, [navigate])

  const refreshAll = useCallback(async () => {
    await loadPending()
    if (activeTab === 'history' || history.length > 0) {
      await loadHistory()
    }
    setRefreshKey(k => k + 1)
  }, [loadPending, loadHistory, activeTab, history.length])

  // K7/6.1: Nur weiterschalten, wenn der Fall ZUVOR offen war und nach einem
  // Refresh verschwunden ist (Aktion/Konflikt). Frische Deep-Links auf
  // abgeschlossene Fälle und Verlauf-Auswahl bleiben read-only stehen.
  const prevPendingIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!selectedId || loading) return
    if (casePending.some(p => p.id === selectedId)) {
      setActiveTab('pending')
      return
    }
    if (!prevPendingIdsRef.current.has(selectedId)) {
      setActiveTab('history')
    }
  }, [selectedId, casePending, loading])

  useEffect(() => {
    const currentIds = new Set(casePending.map(p => p.id))
    if (
      selectedId
      && !loading
      && prevPendingIdsRef.current.has(selectedId)
      && !currentIds.has(selectedId)
    ) {
      if (casePending.length === 0) {
        navigate('/director/decisions', { replace: true })
      } else {
        navigate(`/director/decisions/${casePending[0]!.id}`, { replace: true })
      }
    }
    prevPendingIdsRef.current = currentIds
  }, [selectedId, casePending, loading, navigate])

  const isDirectorDisabled = config != null && config.director_enabled === false
    && pending.length === 0 && history.length === 0

  // Vorheriger/Nächster über die offene Liste (P5.2)
  const pendingIds = useMemo(() => casePending.map(p => p.id), [casePending])
  const selectedIdx = selectedId ? pendingIds.indexOf(selectedId) : -1
  const readOnly = selectedId != null && !pendingIds.includes(selectedId)

  if (loading && pending.length === 0 && !error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] min-h-0 overflow-hidden">
      {/* Master-Liste: mobile vollbreit, sonst 340px (E5) */}
      <div className={`${selectedId ? 'hidden lg:flex' : 'flex'} w-full lg:w-[340px] lg:shrink-0 min-h-0 flex-col overflow-hidden`}>
        {/* Fallakte v3 (A2): Ladefehler der Liste sichtbar machen + Retry */}
        {error && (
          <div className="m-3 flex items-center gap-2 rounded-lg bg-red-100 text-red-700 px-3 py-2 text-xs" role="alert">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              type="button"
              onClick={() => { setError(null); void loadPending() }}
              className="font-semibold underline hover:no-underline"
            >
              Erneut
            </button>
          </div>
        )}
        <CaseListPane
          pending={casePending}
          history={history}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          selectedId={selectedId}
          onSelect={selectCase}
          pendingLoading={loading}
          historyLoading={historyLoading}
        />
      </div>

      {/* Detail: mobile als Overlay (fixed), desktop flex-1 */}
      {selectedId && (
        <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden max-lg:fixed max-lg:inset-0 max-lg:top-14 max-lg:z-40 max-lg:bg-gray-50">
          <CaseDetailPane
            requestId={selectedId}
            config={config}
            readOnly={readOnly}
            hasPrev={selectedIdx > 0}
            hasNext={selectedIdx >= 0 && selectedIdx < pendingIds.length - 1}
            onPrev={() => selectCase(pendingIds[selectedIdx - 1]!)}
            onNext={() => selectCase(pendingIds[selectedIdx + 1]!)}
            onBack={deselect}
            onChanged={refreshAll}
            refreshKey={refreshKey}
          />
        </div>
      )}
      {!selectedId && (
        <div className="hidden lg:flex flex-1 min-w-0 bg-gray-50 items-center justify-center">
          <div className="text-center text-gray-400 max-w-xs">
            {isDirectorDisabled ? (
              <div className="flex items-start gap-2 text-left bg-yellow-100 text-yellow-800 rounded-lg px-4 py-3 text-xs">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Das Director-Feature ist für diese Schule deaktiviert.</span>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium">Fall auswählen</p>
                <p className="text-xs mt-1">
                  Links einen Fall wählen, um die Fallakte mit Infoline, Lektionen,
                  Dokumenten und Timeline zu öffnen.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
